import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

/**
 * KanaOriginChip — a small etymological chip that lives on the cards of
 * kanji that are the historical SOURCE of a katakana (the man'yougana
 * the kana was simplified from).
 *
 * Three signals are surfaced because three different learners care
 * about three different things:
 *   1. The kana itself ("カ") — recognition.
 *   2. The sound match badge — "still reads か" — when the kanji's
 *      modern reading still includes the kana sound, the etymology is
 *      a live mnemonic, not just historical trivia.
 *   3. A phonetic-driver badge + family size — when the kanji also
 *      drives a phonetic family, the kana points the learner to a
 *      whole cluster of related kanji (the strongest case).
 *
 * The chip stays small in the page — the rich detail (visual link
 * description) lives in a popover so users can engage on their terms.
 */
export default function KanaOriginChip({ info }) {
  if (!info) return null;
  const { kana, sound, sound_retained, visual_link, is_phonetic_driver, family_size } = info;
  return (
    <div className="border-b border-borderDefault px-4 md:px-7 py-3" data-testid="kana-origin-chip">
      <p className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans mb-2">
        katakana origin
      </p>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`kana-origin-trigger-${kana}`}
            className="inline-flex items-center gap-3 border border-shu/40 px-3 py-2 bg-shu/5 hover:bg-shu/10 hover:border-shu/70 transition-colors cursor-help text-left"
          >
            <span className="font-serif text-3xl text-shu leading-none">{kana}</span>
            <span className="flex flex-col gap-0.5">
              <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-sumiSoft">
                kana &lt;– this kanji
              </span>
              <span className="font-sans text-sm text-sumi">
                the katakana <span className="text-shu font-serif text-base">{kana}</span> was
                simplified from this kanji
              </span>
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-96 bg-washi border-borderDefault text-sumi p-4 space-y-3"
        >
          <p className="font-serif text-base text-sumi leading-snug">
            <span className="text-shu text-2xl font-serif">{kana}</span>
            <span className="ml-3 font-sans text-xs uppercase tracking-[0.2em] text-sumiSoft">
              reads {sound}
            </span>
          </p>

          {/* Visual link — always shown; it's the etymology's most
              concrete claim. */}
          <div>
            <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-sumiSoft mb-1">
              visual link
            </p>
            <p className="font-sans text-sm text-sumi/90">{visual_link}</p>
          </div>

          {/* Sound retention — only ever true OR informative. */}
          <div>
            <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-sumiSoft mb-1">
              sound retention
            </p>
            <p className="font-sans text-sm text-sumi/90">
              {sound_retained ? (
                <>
                  <span className="text-shu font-medium">Yes</span> — this kanji still has
                  a reading that starts with{" "}
                  <span className="font-serif text-shu">{sound}</span>. The mnemonic still
                  works.
                </>
              ) : (
                <>
                  <span className="text-sumiSoft">No longer</span> — the kanji&apos;s modern
                  readings have drifted from{" "}
                  <span className="font-serif">{sound}</span>. Historical link only.
                </>
              )}
            </p>
          </div>

          {/* Phonetic-driver bonus — only if true. */}
          {is_phonetic_driver && family_size > 0 && (
            <div>
              <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-sumiSoft mb-1">
                bonus · phonetic family
              </p>
              <p className="font-sans text-sm text-sumi/90">
                This kanji also drives a phonetic family of{" "}
                <span className="text-shu font-medium">{family_size} kanji</span>. Once you
                know <span className="font-serif text-shu">{kana}</span> →{" "}
                <span className="font-serif text-shu">{info.kana_origin?.kanji || ""}</span>,
                the whole family becomes guessable from sound alone.
              </p>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
